/**
 * ClassifierForm — type-specific fields for a "classifier" service
 * (KAN-157 split out of CreateServiceDialog.tsx).
 *
 * Renders the tariff table (work/variant/price/unit), the add-row button,
 * validation error, and the optional post-formula editor (system variable
 * «базовая_цена»).
 */
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, X } from 'lucide-react';
import { FormulaEditor } from '@/components/services/FormulaEditor';
import type { PricingRow } from './types';

interface ClassifierFormProps {
  pricingRows: PricingRow[];
  onAddPricingRow: () => void;
  onRemovePricingRow: (index: number) => void;
  onUpdatePricingRow: (index: number, field: keyof PricingRow, value: string) => void;
  pricingError: string | null;
  postFormulaEnabled: boolean;
  onPostFormulaEnabledChange: (enabled: boolean) => void;
  postFormula: string;
  onPostFormulaChange: (value: string) => void;
}

export function ClassifierForm({
  pricingRows,
  onAddPricingRow,
  onRemovePricingRow,
  onUpdatePricingRow,
  pricingError,
  postFormulaEnabled,
  onPostFormulaEnabledChange,
  postFormula,
  onPostFormulaChange,
}: ClassifierFormProps): JSX.Element {
  return (
    <div className="space-y-2">
      <Label>Тарифы</Label>
      <div data-testid="table-pricing-data">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-2 font-medium">Работа</th>
              <th className="py-2 pr-2 font-medium">Вариант</th>
              <th className="py-2 pr-2 font-medium">Цена</th>
              <th className="py-2 pr-2 font-medium">Ед. изм.</th>
              <th className="py-2 w-8"></th>
            </tr>
          </thead>
          <tbody>
            {pricingRows.map((row, index) => (
              <tr key={index} data-testid={`pricing-row-${index}`} className="border-b">
                <td className="py-1.5 pr-2">
                  <Input
                    data-testid="pricing-name"
                    value={row.name}
                    onChange={(e) => onUpdatePricingRow(index, 'name', e.target.value)}
                    placeholder="Работа"
                    className="h-8"
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <Input
                    data-testid="pricing-variant"
                    value={row.variant}
                    onChange={(e) => onUpdatePricingRow(index, 'variant', e.target.value)}
                    placeholder="Вариант"
                    className="h-8"
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <Input
                    data-testid="pricing-price"
                    value={row.price}
                    onChange={(e) => onUpdatePricingRow(index, 'price', e.target.value)}
                    placeholder="Цена"
                    className="h-8"
                    type="number"
                  />
                </td>
                <td className="py-1.5 pr-2">
                  <Input
                    data-testid="pricing-unit"
                    value={row.unit}
                    onChange={(e) => onUpdatePricingRow(index, 'unit', e.target.value)}
                    placeholder="Ед."
                    className="h-8"
                  />
                </td>
                <td className="py-1.5">
                  <button
                    data-testid="remove-row"
                    onClick={() => onRemovePricingRow(index)}
                    className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button
        data-testid="add-pricing-row"
        variant="outline"
        size="sm"
        onClick={onAddPricingRow}
        type="button"
      >
        <Plus className="h-3.5 w-3.5 mr-1" />
        Добавить тариф
      </Button>
      {pricingError && <p className="text-sm text-red-500">{pricingError}</p>}

      {/* Post-formula toggle */}
      <label
        data-testid="post-formula-toggle"
        className="flex items-center gap-2 cursor-pointer select-none pt-2"
      >
        <input
          type="checkbox"
          checked={postFormulaEnabled}
          onChange={(e) => onPostFormulaEnabledChange(e.target.checked)}
          className="rounded border-gray-300"
        />
        <span className="text-sm font-medium">Пост-формула (необязательно)</span>
      </label>
      {postFormulaEnabled && (
        <FormulaEditor
          value={postFormula}
          onChange={onPostFormulaChange}
          variables={[
            {
              name: 'базовая_цена',
              label: 'Базовая цена',
              unit: 'руб',
              source: 'system' as const,
            },
          ]}
          mode="postprocess"
        />
      )}
    </div>
  );
}
